/*
good ol' linked list! 
object structure:
linkedList: {
  _head: UUID,
  _tail: UUID,
  _items: {
    ${UUID}: {
      prev: UUID,
      next: UUID,
      data: UUID of element in dictionary where element is stored,
    },
  }
}
*/

let LinkedListMutator = {}

LinkedListMutator.getNewLinkedList = () => {
  return {
    _head: null,
    _tail: null,
    _count: 0,
    _items: {},
  }
}

// ------- //

// private function
let getNewLink = (prev = null, next = null, data = null) => {
  return {
    prev,
    next,
    data,
  }
}

// all the ifs could be avoided if we had variable-variables / pointers

// private function, depends on remove, insert, shift to handle values and count
// id MUST already be added to linkedList
let removeLinkage = (linkedList, id) => {
  // do NOT check if linkedList._items[id] exists! 
  //  other functions change count and depend on linkedList._items[id] existing
  //  so if it doesn't we want errors in the console
  if (linkedList._items[id].prev) {
    linkedList._items[ linkedList._items[id].prev ].next = linkedList._items[id].next // could be null if it's the tail, we're fine with that
  } else {
    // it's the head
    linkedList._head = linkedList._items[id].next // could be null if it's the only one, we're fine with that
  }
  
  if (linkedList._items[id].next) {
    linkedList._items[ linkedList._items[id].next ].prev = linkedList._items[id].prev // could be null if it's the head, we're fine with that
  } else {
    // it's the tail
    linkedList._tail = linkedList._items[id].prev // could be null if it's the only one, we're fine with that
  }

  linkedList._items[id].prev = null
  linkedList._items[id].next = null
  return linkedList
}

// private function, depends on remove, insert, shift to handle values and count
// id MUST already be added to linkedList
let insertLinkage = (linkedList, atId, id) => {
  // do NOT check if linkedList._items[id] exists! 
  //  other functions change count and depend on linkedList._items[id] existing
  //  so if it doesn't we want errors in the console
  if (!atId) {
    // adding to the end
    if (linkedList._tail) {
      linkedList._items[id].prev = linkedList._tail
      linkedList._items[ linkedList._tail ].next = id
    } else {
      // only element
      linkedList._items[id].prev = null
      linkedList._head = id
    }
    linkedList._items[id].next = null
    linkedList._tail = id
  } else {
    // inserting to the head or body
    if (linkedList._items[atId].prev) {
      // body
      linkedList._items[id].prev = linkedList._items[atId].prev
      linkedList._items[ linkedList._items[atId].prev ].next = id
    } else {
      // head
      linkedList._items[id].prev = null // just to be sure
      linkedList._head = id
    }
    linkedList._items[id].next = atId
    linkedList._items[atId].prev = id
  }
  return linkedList
}

// ------- //

LinkedListMutator.getCount = (linkedList) => {
  return linkedList._count
}

LinkedListMutator.remove = (linkedList, id) => {
  if (linkedList._items[id]) {
    linkedList = removeLinkage(linkedList, id)
    delete linkedList._items[id]
    linkedList._count --
  }
  return linkedList
}

LinkedListMutator.insertAt = (linkedList, atId, insertId, insertData) => {
  if (linkedList._items[insertId]) {
    // we already have this entry, update the value then shift it
    linkedList._items[insertId].data = insertData
    return LinkedListMutator.shiftTo(linkedList, atId, insertId)
  }
  // new entry
  if (!atId || !linkedList._items[atId]) atId = null // if blank or invalid atId: add to end
  linkedList._items[insertId] = getNewLink(null, null, insertData)
  linkedList = insertLinkage(linkedList, atId, insertId)
  linkedList._count++
  return linkedList
}

LinkedListMutator.shiftTo = (linkedList, atId, id) => {
  if (
    !linkedList._items[id]
    || id === atId
  ) return linkedList
  linkedList = removeLinkage(linkedList, id)
  linkedList = insertLinkage(linkedList, atId, id)
  return linkedList
}

LinkedListMutator.insertAfter = (linkedList, afterId, insertId, insertData) => {
  let atId = null
  if (afterId && linkedList._items[afterId]) {
    atId = linkedList._items[afterId].next // could be null, we're fine with that
  } // else insert at the end
  return LinkedListMutator.insertAt(linkedList, atId, insertId, insertData)
}

LinkedListMutator.insertAtHead = (linkedList, insertId, insertData) => {
  return LinkedListMutator.insertAt(linkedList, linkedList._head, insertId, insertData)
}

LinkedListMutator.insertAtTail = (linkedList, insertId, insertData) => {
  return LinkedListMutator.insertAfter(linkedList, null, insertId, insertData)
}

LinkedListMutator.removeHead = (linkedList) => {
  return LinkedListMutator.remove(linkedList, linkedList._head)
}

LinkedListMutator.removeTail = (linkedList) => {
  return LinkedListMutator.remove(linkedList, linkedList._tail)
}

LinkedListMutator.shiftToHead = (linkedList, id) => {
  return LinkedListMutator.shiftTo(linkedList, linkedList._head, id)
}

LinkedListMutator.shiftToTail = (linkedList, id) => {
  return LinkedListMutator.shiftTo(linkedList, null, id)
}

LinkedListMutator.getAt = (linkedList, id) => {
  let data = null
  if (linkedList._items[id]) data = linkedList._items[id].data
  return {id: id, value: data}
}

LinkedListMutator.getHead = (linkedList) => {
  return LinkedListMutator.getAt(linkedList, linkedList._head)
}

LinkedListMutator.getTail = (linkedList) => {
  return LinkedListMutator.getAt(linkedList, linkedList._tail)
}

LinkedListMutator.getPrevOf = (linkedList, id) => {
  return LinkedListMutator.getAt(linkedList, linkedList._items[id].prev)
}

LinkedListMutator.getNextOf = (linkedList, id) => {
  return LinkedListMutator.getAt(linkedList, linkedList._items[id].next)
}

LinkedListMutator.iterate = (linkedList, lambda, isReverse) => {
  let item
  if  (isReverse) {
    item = LinkedListMutator.getTail(linkedList)
  } else {
    item = LinkedListMutator.getHead(linkedList)
  }
  while (null !== item && null !== item.value) {
    lambda(item.value)
    if (isReverse) {
      item = LinkedListMutator.getPrevOf(linkedList, item.id)
    } else {
      item = LinkedListMutator.getNextOf(linkedList, item.id)
    }
  }
}

Object.freeze(LinkedListMutator)
export {LinkedListMutator}
